// FormShell — the two presentational pieces of the shared form body (ADR-038 §2),
// driven by a useFormShell return. They are container-AGNOSTIC: the same pair sits
// inside a SlideOver, a Modal, a page, or an inline section — the container is the
// only variable.
//
//   <FormBody>   the `<form>` + the refusal line above the fields (ADR-084 §6 — the
//                one slot every container uses: a refused save's words, the stale
//                line with Load the latest, the gone line with Close, the latest
//                showing) + the field stack (children), read-only once the row is gone.
//   <FormFooter> Cancel (secondary, start) → Submit (primary, end, loading; disabled
//                once the row is gone).
//
// Body and footer are split across two slots in a drawer/page, so the footer's
// Submit Button reaches its `<form>` by the HTML `form={formId}` association rather
// than DOM nesting — one form, two slots. Cancel always routes through the dirty
// guard (shell.requestLeave), so closing with unsaved edits prompts to discard —
// except over a row that is gone, where nothing is left to save it to.

import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { RefusalLine } from './RefusalLine.js';
import type { UseFormShellReturn } from './useFormShell.js';

export interface FormBodyProps<TValues extends FieldValues> {
  shell: UseFormShellReturn<TValues>;
  /** The field stack — Label + Input/Controller per FRONTEND-PATTERNS §Forms. */
  children: ReactNode;
  /** Extra classes for the field stack (default vertical rhythm). */
  className?: string;
  /** The container's leave for the gone line's Close (the drawer closes, the inline section returns to its read). */
  onGone?: () => void;
}

export function FormBody<TValues extends FieldValues>({ shell, children, className = '', onGone }: FormBodyProps<TValues>) {
  return (
    <form id={shell.formId} onSubmit={shell.onFormSubmit} noValidate className="space-y-4">
      <RefusalLine line={shell.line} onClose={onGone ? () => shell.discardAndLeave(onGone) : undefined} />
      {/* A gone row keeps the typing readable (to copy out) but takes no more: the native way, one attribute. */}
      <fieldset disabled={shell.gone} className={`min-w-0 space-y-4 ${className}`}>
        {children}
      </fieldset>
    </form>
  );
}

export interface FormFooterProps<TValues extends FieldValues> {
  shell: UseFormShellReturn<TValues>;
  /** The raw leave action (close the drawer / navigate back); Cancel routes it
   *  through the dirty guard so unsaved edits prompt to discard first. */
  onCancel: () => void;
  /** The submit verb ("Create site", "Save changes") — i18n'd by the caller. */
  submitLabel: string;
  /** Defaults to common.actions.cancel. */
  cancelLabel?: string;
  /** Gates the primary action until the form's required choices are made
   *  (decision forms disable Save rather than surprise-validate). */
  submitDisabled?: boolean;
}

export function FormFooter<TValues extends FieldValues>({
  shell,
  onCancel,
  submitLabel,
  cancelLabel,
  submitDisabled = false,
}: FormFooterProps<TValues>) {
  const { t } = useTranslation('common');
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => (shell.gone ? shell.discardAndLeave(onCancel) : shell.requestLeave(onCancel))}
        disabled={shell.isSubmitting}
      >
        {cancelLabel ?? t('actions.cancel')}
      </Button>
      <Button
        type="submit"
        form={shell.formId}
        variant="primary"
        loading={shell.isSubmitting}
        disabled={submitDisabled || shell.gone}
      >
        {submitLabel}
      </Button>
    </>
  );
}
